import React, { useState } from "react";
import apiService from "../../services/apiService";

/* global Office */

const SalesforceActions = ({ onNavigateToTab, sfContext, sfAuthError, emailInfo, sfSession, refreshSfToken }) => {
    const [isLoggingEmail, setIsLoggingEmail] = useState(false);

    const [logStatus, setLogStatus] = useState(null); // { type: 'success' | 'error', message: string }
    const [message, setMessage] = useState({ type: "", text: "" });
    const handleOpenClient = () => {
        if (!sfContext || Object.keys(sfContext).length === 0 || sfContext.matchFound === false) {
            setMessage({ type: "error", text: "Salesforce account not selected." });

            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }
        if (sfAuthError) {
            setMessage(`Salesforce access restricted: ${sfAuthError.error || "Authentication failure"}.`);
            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }
        if (!sfContext || Object.keys(sfContext).length === 0 || sfContext.matchFound === false) {
            setMessage("Salesforce account not selected.");
            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }

        const instanceUrl = sfSession?.instanceUrl;
        if (instanceUrl && sfContext?.accountId) {
            const url = `${instanceUrl}/lightning/r/Account/${sfContext.accountId}/view`;
            window.open(url, '_blank');
        } else {
            setMessage("Salesforce session not found. Please wait for connection.");
            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
        }

    };

    const handleLogEmail = async () => {

        if (!sfContext || Object.keys(sfContext).length === 0 || sfContext.matchFound === false) {
            setMessage({ type: "error", text: "Salesforce account not selected." });

            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }
        if (sfAuthError) {
            setMessage("Salesforce access restricted.");
            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }

        let accessToken = sfSession?.accessToken;
        let instanceUrl = sfSession?.instanceUrl;

        if (!accessToken || !instanceUrl) {
            const refreshed = await refreshSfToken?.();
            if (refreshed) {
                accessToken = refreshed.accessToken;
                instanceUrl = refreshed.instanceUrl;
            } else {
                setMessage("Salesforce session expired. Please retry.");
                setTimeout(() => setMessage({ type: "", text: "" }), 5000);
                return;
            }
        }

        if (!sfContext || Object.keys(sfContext).length === 0 || sfContext.matchFound === false) {
            setMessage("Salesforce account not selected.");
            setTimeout(() => setMessage({ type: "", text: "" }), 5000);
            return;
        }


        setIsLoggingEmail(true);
        setLogStatus(null);

        try {
            const item = Office.context?.mailbox?.item;
            if (!item) {
                throw new Error("Office mailbox item not available.");
            }

            // Helper to get body text as a Promise
            const getEmailBody = () => {
                return new Promise((resolve) => {
                    item.body.getAsync("text", (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(result.value);
                        } else {
                            resolve("Body text could not be retrieved.");
                        }
                    });
                });
            };

            const bodyText = await getEmailBody();
            let emailDate = new Date().toISOString().replace('T', ' ').split('.')[0];
            let subject = item.subject || emailInfo?.subject || "No Subject";

            // Extract recipients
            const formatRecipients = (recipients) => (recipients || []).map(r => r.emailAddress).join(", ");
            const receiverEmail = formatRecipients(item.to);
            const cc = formatRecipients(item.cc);
            const bcc = formatRecipients(item.bcc);

            if (item.dateTimeCreated) {
                emailDate = new Date(item.dateTimeCreated).toISOString().replace('T', ' ').split('.')[0];
            }

            const payload = {
                instance_url: instanceUrl,
                access_token: accessToken,
                senderEmail: emailInfo?.email || "unknown@example.com",
                senderName: emailInfo?.from || "Unknown Sender",
                receiverEmail: receiverEmail,
                cc: cc,
                bcc: bcc,
                subject: subject,
                body: bodyText,
                emailDate: emailDate,
                accountId: sfContext.accountId
            };

            const response = await apiService.logEmailToSalesforce(payload);

            if (response.error) {
                setLogStatus({ type: 'error', message: response.error });
            } else {
                setLogStatus({ type: 'success', message: 'Email logged successfully!' });
                setTimeout(() => setLogStatus(null), 3000);
            }
        } catch (error) {
            console.error("Log email error:", error);
            setLogStatus({ type: 'error', message: 'Failed to log email activity.' });
            // setLoading(false);
        } finally {
            setIsLoggingEmail(false);
            // setLoading(false);
        }
    };

    return (
        <div className="action-card">
            <div className="action-card-header">
                <span className="action-icon sf-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="#00a1e0" />
                    </svg>
                </span>
                <span className="action-title">Salesforce Actions</span>
            </div>

            <div className="action-card-body">
                {message.text && (
                    <div className={`action-message ${message.type}`}>
                        {message.text}
                    </div>
                )}
                {logStatus && (
                    <div className={`status-message ${logStatus.type}`} style={{
                        padding: '8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        marginBottom: '10px',
                        backgroundColor: logStatus.type === 'success' ? '#ecfdf5' : '#fef2f2',
                        color: logStatus.type === 'success' ? '#065f46' : '#991b1b',
                        border: `1px solid ${logStatus.type === 'success' ? '#10b981' : '#f87171'}`
                    }}>
                        {logStatus.message}
                    </div>
                )}

                <button
                    className="action-btn-secondary"
                    onClick={() => onNavigateToTab?.("AI Analysis")}
                >
                    <span className="btn-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                    </span>
                    Create Case (Use AI Analysis)
                </button>

                <button
                    className="action-btn-secondary"
                    onClick={handleLogEmail}
                    disabled={isLoggingEmail}
                >
                    <span className="btn-icon">
                        {isLoggingEmail ? (
                            <span className="spinner-small" style={{ width: '14px', height: '14px', border: '2px solid #cbd5e1', borderTopColor: '#0078d4', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                        ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                        )}
                    </span>
                    {isLoggingEmail ? "Logging..." : "Log Email as Activity"}
                </button>

                <button className="action-btn-secondary" onClick={handleOpenClient}>
                    <span className="btn-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    </span>
                    Open Client in Salesforce
                </button>
            </div>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default SalesforceActions;
